import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PlayCircleFilledOutlinedIcon from '@mui/icons-material/PlayCircleFilledOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';

const HEADER_GREEN = '#2CA900';
const ACCENT_ORANGE = '#F59E0B';

const Pad2 = (n) => String(n).padStart(2, '0');

/** 로컬 YYYY-MM-DD */
const ToDateInput = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${Pad2(d.getMonth() + 1)}-${Pad2(d.getDate())}`;
};

/** 로컬 HH:mm (time input) */
const ToTimeInput = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${Pad2(d.getHours())}:${Pad2(d.getMinutes())}`;
};

const ParseLocalDateTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const t = timeStr && timeStr.length >= 5 ? timeStr : '00:00';
  const iso = `${dateStr}T${t.length === 5 ? `${t}:00` : t}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * 근로자 의견 받기 기간 설정
 */
export default function WorkerOpinionReceptionPeriodModal({
  open,
  onClose,
  onSubmit,
  loading = false,
}) {
  const defaults = useMemo(() => {
    const now = new Date();
    const start = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      9,
      0,
      0,
      0
    );
    const end = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      18,
      0,
      0,
      0
    );
    return {
      startDate: ToDateInput(start),
      startTime: ToTimeInput(start),
      endDate: ToDateInput(end),
      endTime: ToTimeInput(end),
    };
  }, []);

  const [startDate, setStartDate] = useState(defaults.startDate);
  const [startTime, setStartTime] = useState(defaults.startTime);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [endTime, setEndTime] = useState(defaults.endTime);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setStartDate(defaults.startDate);
    setStartTime(defaults.startTime);
    setEndDate(defaults.endDate);
    setEndTime(defaults.endTime);
    setError('');
  }, [open, defaults]);

  const HandleSubmit = async () => {
    setError('');
    const s = ParseLocalDateTime(startDate, startTime);
    const e = ParseLocalDateTime(endDate, endTime);
    if (!s || !e) {
      setError('시작·종료 일시를 모두 입력해 주세요.');
      return;
    }
    if (e.getTime() <= s.getTime()) {
      setError('종료 일시는 시작 일시보다 이후여야 합니다.');
      return;
    }
    const ok = await onSubmit?.({
      workerOpiionsStartDate: s,
      workerOpiionsEndDate: e,
    });
    if (ok === true) onClose?.();
  };

  return (
    <Dialog
      open={!!open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 2.5,
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="h6" component="span" fontWeight={800}>
          근로자 의견 받기 기간 설정
        </Typography>
        <IconButton
          aria-label="닫기"
          onClick={onClose}
          edge="end"
          size="small"
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 5, pb: 2 }}>
        <Alert
          icon={<InfoOutlinedIcon fontSize="inherit" />}
          severity="success"
          sx={{
            mt: 1,
            mb: 2.5,
            alignItems: 'center',
            bgcolor: 'rgba(44, 169, 0, 0.12)',
            color: 'text.primary',
            border: `1px solid ${HEADER_GREEN}`,
            '& .MuiAlert-icon': { color: HEADER_GREEN },
          }}
        >
          지정된 기간 동안 근로자들이 자유롭게 의견을 제출할 수 있습니다. 기간
          설정 후 즉시 활성화됩니다.
        </Alert>

        {error ? (
          <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
            {error}
          </Typography>
        ) : null}

        <Stack spacing={2.5}>
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <PlayCircleFilledOutlinedIcon sx={{ color: ACCENT_ORANGE }} />
              <Typography variant="subtitle2" fontWeight={800}>
                시작 일시
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="시작 날짜"
                type="date"
                size="small"
                fullWidth
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
              <TextField
                label="시간"
                type="time"
                size="small"
                fullWidth
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Stack>
          </Box>

          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <StopCircleOutlinedIcon sx={{ color: ACCENT_ORANGE }} />
              <Typography variant="subtitle2" fontWeight={800}>
                종료 일시
              </Typography>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="종료 날짜"
                type="date"
                size="small"
                fullWidth
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
              <TextField
                label="시간"
                type="time"
                size="small"
                fullWidth
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}
      >
        <Button
          variant="contained"
          onClick={HandleSubmit}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            bgcolor: ACCENT_ORANGE,
            '&:hover': { bgcolor: '#D97706' },
          }}
        >
          설정 완료
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            borderColor: HEADER_GREEN,
            color: HEADER_GREEN,
          }}
        >
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
}
